import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Link,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from "@mui/material";
import { PimoReactComponent } from "@pimo/pimo-app-builder";
import { SupportedLanguage } from "@pimo/pimo-l10n";
import { useState } from "react";

import {
  ENTERPRISE_CONTACT_EMAIL,
  PRICING_PAGE_URL,
  TERMS_OF_USE_URL,
} from "../constants";
import {
  ADDITIONAL_TOOL_PRICE_EUR,
  BillingAddress,
  Plan,
  PlanKey,
} from "../types/entities";
import {
  BillingAddressDraft,
  BillingAddressForm,
} from "./billing-address-form";
import { ModalLegalFooter } from "./modal-legal-footer";
import {
  StripeCheckoutForm,
  StripeCheckoutFormEvent,
  StripeCheckoutFormPayload,
} from "./stripe-checkout-form";

export interface SelectPlanModalState {
  isOpen: boolean;
  plans: Plan[];
  defaultPlanKey?: PlanKey;
  billingAddress?: BillingAddress | null;
  isSubmitting?: boolean;
  errorMessage?: string;
  language: SupportedLanguage;
  /** Step 1 ("plan", the default) is the existing plan + billing picker;
   * step 2 ("payment") swaps in `StripeCheckoutForm`, in the same dialog —
   * no close/reopen — per the UX/UI design spec comment on #2785. */
  checkoutStep?: "plan" | "payment";
  /** The Stripe PaymentIntent client secret for step 2, once the backend
   * has started an incomplete Stripe subscription (#2792). */
  clientSecret?: string;
}

export type SelectPlanModalEvent =
  "close-select-plan-modal" | "submit-select-plan" | StripeCheckoutFormEvent;

export interface SubmitSelectPlanPayload {
  planKey: PlanKey;
  billingAddress: BillingAddressDraft;
}

export type SelectPlanModalPayload =
  SubmitSelectPlanPayload | StripeCheckoutFormPayload;

export const SelectPlanModal: PimoReactComponent<
  SelectPlanModalState,
  SelectPlanModalEvent,
  SelectPlanModalPayload
> = ({
  fireEvent,
  isOpen,
  plans,
  defaultPlanKey,
  billingAddress,
  isSubmitting,
  errorMessage,
  language,
  checkoutStep,
  clientSecret,
}) => {
  const [selected, setSelected] = useState<PlanKey | undefined>(
    defaultPlanKey ?? "advanced"
  );
  const [agreed, setAgreed] = useState(false);
  const [billingValid, setBillingValid] = useState(false);
  const [billingDraft, setBillingDraft] = useState<BillingAddressDraft | null>(
    null
  );

  const canSubmit =
    !!selected && agreed && billingValid && !isSubmitting && billingDraft;

  const isPaymentStep = checkoutStep === "payment" && !!clientSecret;
  const selectedPlan = plans.find((plan) => plan.key === selected);

  /**
   * `StripeCheckoutForm` declares its own, narrower event/payload types
   * (so it stays independently testable — see its spec) that are a subset
   * of this modal's. The cast here is the one place that bridges them; it
   * is safe because every `StripeCheckoutFormEvent` is also a
   * `SelectPlanModalEvent` (see the union above), and `selectPlanModalComponent`
   * (not a separately-registered component) is what actually receives
   * these events in `home-portal-plugin.ts`.
   */
  const fireCheckoutEvent = (
    event: StripeCheckoutFormEvent,
    payload?: StripeCheckoutFormPayload
  ) => fireEvent?.(event, payload);

  return (
    <Dialog
      open={isOpen}
      onClose={() =>
        fireEvent?.(
          isPaymentStep ? "cancel-payment" : "close-select-plan-modal"
        )
      }
      slotProps={{
        transition: {
          onEnter: () => {
            setSelected(defaultPlanKey ?? "advanced");
            setAgreed(false);
          },
        },
      }}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{ fontWeight: 700, pb: 0.5 }}>Select Plan</DialogTitle>
      <Typography variant="body2" color="text.secondary" sx={{ px: 3, pb: 1 }}>
        {isPaymentStep
          ? "Confirm your payment to activate the plan."
          : "Select a plan to activate tools."}
      </Typography>
      <DialogContent sx={{ pt: 1 }}>
        {isPaymentStep ? (
          <StripeCheckoutForm
            fireEvent={fireCheckoutEvent}
            clientSecret={clientSecret ?? ""}
            planName={selectedPlan?.name ?? ""}
            monthlyPriceEur={selectedPlan?.basePriceEur ?? 0}
            isProcessing={!!isSubmitting}
            errorMessage={errorMessage}
            language={language}
          />
        ) : (
          <Stack spacing={2}>
            {/* Plan list */}
            <RadioGroup
              value={selected ?? ""}
              onChange={(e) => setSelected(e.target.value as PlanKey)}
            >
              <Stack spacing={1}>
                {plans.map((plan) => (
                  <Box
                    key={plan.key}
                    sx={{
                      border: "1px solid",
                      borderColor:
                        selected === plan.key ? "primary.main" : "#e0e0e0",
                      borderRadius: 2,
                      p: 2,
                    }}
                  >
                    <FormControlLabel
                      value={plan.key}
                      control={<Radio size="small" />}
                      sx={{ m: 0, width: "100%" }}
                      label={
                        <Box>
                          <Typography
                            variant="subtitle2"
                            sx={{ fontWeight: 700, lineHeight: 1.3 }}
                          >
                            {plan.name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {plan.basePriceEur} € per month (Up to{" "}
                            {plan.userLimit} Users)
                          </Typography>
                        </Box>
                      }
                    />
                  </Box>
                ))}

                {/* Enterprise – contact sales */}
                <Box
                  sx={{
                    border: "1px dashed #bdbdbd",
                    borderRadius: 2,
                    p: 2,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    bgcolor: "#f5f5f5",
                    opacity: 0.75,
                  }}
                >
                  <Radio size="small" disabled sx={{ p: 0.5 }} />
                  <Box>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: 700,
                        lineHeight: 1.3,
                        color: "text.disabled",
                      }}
                    >
                      Enterprise
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Available on request –{" "}
                      <Link
                        href={`mailto:${ENTERPRISE_CONTACT_EMAIL}`}
                        underline="hover"
                      >
                        contact us
                      </Link>
                    </Typography>
                  </Box>
                </Box>
              </Stack>
            </RadioGroup>

            {/* Tool pricing info */}
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: 1,
                border: "1px solid #e0e0e0",
                borderRadius: 2,
                p: 1.5,
                bgcolor: "#fafafa",
              }}
            >
              <InfoOutlinedIcon
                fontSize="small"
                sx={{ color: "primary.main", mt: 0.1 }}
              />
              <Typography variant="body2" color="text.secondary">
                You can activate one tool for free.
                <br />
                Additional tools costs {ADDITIONAL_TOOL_PRICE_EUR} € per month.
              </Typography>
            </Box>

            {/* Pricing page link */}
            <Typography variant="body2">
              <Link
                href={PRICING_PAGE_URL}
                target="_blank"
                rel="noopener"
                underline="hover"
                sx={{ color: "primary.main", fontWeight: 500 }}
              >
                View full pricing details →
              </Link>
            </Typography>

            {/* Terms checkbox */}
            <FormControlLabel
              control={
                <Checkbox
                  checked={agreed}
                  onChange={(e) => setAgreed(e.target.checked)}
                  size="small"
                />
              }
              label={
                <Typography variant="body2">
                  I confirm the selection of paid plan and agree to the{" "}
                  <Link href={TERMS_OF_USE_URL} target="_blank" rel="noopener">
                    terms of use
                  </Link>
                  .
                </Typography>
              }
            />

            {/* Billing address */}
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                Billing address
              </Typography>
              <BillingAddressForm
                address={billingAddress}
                forceEdit={!billingAddress}
                onChange={setBillingDraft}
                onValidityChange={setBillingValid}
                language={language}
              />
            </Box>

            {errorMessage ? (
              <Alert severity="error">{errorMessage}</Alert>
            ) : null}
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 1 }}>
        {isPaymentStep ? (
          <Button
            onClick={() => fireEvent?.("cancel-payment")}
            disabled={!!isSubmitting}
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>
        ) : (
          <>
            <Button
              onClick={() => fireEvent?.("close-select-plan-modal")}
              sx={{ textTransform: "none" }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              disabled={!canSubmit}
              onClick={() => {
                if (!canSubmit || !billingDraft || !selected) return;
                fireEvent?.("submit-select-plan", {
                  planKey: selected,
                  billingAddress: billingDraft,
                });
              }}
              sx={{
                bgcolor: "#ff8c00",
                color: "#fff",
                textTransform: "none",
                boxShadow: "none",
                "&:hover": { bgcolor: "#e67e00" },
                "&.Mui-disabled": { bgcolor: "#f0d4b3", color: "#fff" },
              }}
            >
              {isSubmitting ? "Submitting…" : "Select Plan"}
            </Button>
          </>
        )}
      </DialogActions>
      <ModalLegalFooter language={language} />
    </Dialog>
  );
};
